import { Check, MessageSquare, X } from "lucide-react";

import { Badge } from "@/components/_atoms/badge";
import { Separator } from "@/components/_atoms/separator";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";

import type { DataRequestFieldResponse } from "../../models/data-request.model";
import type { DataRequestFieldDefinition, DataRequestFieldType } from "../create-data-request-dialog/data-request-fields";

interface EntityResponseFieldRowProps {
  field: DataRequestFieldDefinition;
  response: DataRequestFieldResponse | undefined;
  currentValue: string | null;
  showSeparator: boolean;
  resolve: (fieldType: DataRequestFieldType, rawValue: unknown) => string;
}

export function EntityResponseFieldRow({ field, response, currentValue, showSeparator, resolve }: EntityResponseFieldRowProps) {
  const { t } = useDomainTranslation("data-request");

  return (
    <div>
      {showSeparator && <Separator />}
      <div className="flex flex-col gap-1 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{t(field.labelKey)}</span>
          {response ? (
            <span className="flex items-center">
              {response.notPossible ? (
                <Badge variant="outline" className="gap-1 text-xs text-warning-foreground">
                  <X className="h-3 w-3" />
                  {t("detail.responseSheet.notPossible")}
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" />
                  {t("detail.responseSheet.answered")}
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">{t("detail.responseSheet.noChanges")}</span>
          )}
        </div>

        {response && !response.notPossible ? (
          <p
            className={
              response.newValue === null || response.newValue === undefined
                ? "text-sm text-muted-foreground"
                : "text-sm text-foreground"
            }
          >
            {response.newValue === null || response.newValue === undefined
              ? "—"
              : resolve(field.fieldType, response.newValue)}
          </p>
        ) : currentValue ? (
          <p className="text-sm text-muted-foreground">{currentValue}</p>
        ) : null}

        {response?.comment && (
          <div className="mt-1 flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-2">
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-sm text-foreground/80">{response.comment}</span>
          </div>
        )}
      </div>
    </div>
  );
}
